import { useMemo, useState, type ReactNode } from 'react';
import { Ruler } from 'lucide-react';
import { useCatalogItems } from './catalogItems';
import { longestAxis, longestMesh, useMeshDims } from './meshDims';
import { partDbSize } from './partIdentity';
import type { Dims } from './meshAnnotations';
import { useSchemaCrops } from './schemaCropsRun';
import { setPartSize, type PartItem, type PartSize } from './partsRun';
import { fillCatalogSizes } from './catalogItems';
import { cropPages, toolLength, toolReads, usePageAspects, type ToolRead } from './toolSizing';
import { useLanguage } from '../../contexts/LanguageContext';

// HOW BIG THE TOOLS ARE, measured on the manual's drawings — part of Phase 2's Estimate.
//
// A tool the carton does not hold has no measured size anywhere: not in the supplied-parts
// section, which never listed it, and not in the Parts-DB, which has no row for a
// screwdriver somebody already owns. So Phase 5 draws its mesh at whatever size the file
// happened to be, and the assembly has a screwdriver as long as the chair.
//
// The manual answers it for free. It drew the tool beside the screw it is driving, at one
// scale, on one page — and that screw is measured. The whole computation is a ratio, and
// every one of its inputs is an answer already paid for: the boxes from the manual search,
// the pairing from that search's `touches`, the millimetres from the size read. See
// toolSizing, where the arithmetic and its rules live.
//
// IN ESTIMATE, and it was a row of Phase 3's: the same kind of answer as a component's size
// off the photographs — arithmetic over boxes, no call — from the same row of crops. It was
// in Phase 3 only because the search it read lived there.
export function useToolSizes({ docId, file, items }: {
  docId: string;
  file: Blob;
  items: PartItem[];
}) {
  const { t, language } = useLanguage();
  const schema = useSchemaCrops(docId);
  const { items: catalog } = useCatalogItems();

  const tools = useMemo(
    () => items.map((p, i) => (p.tool ? i : -1)).filter((i) => i >= 0),
    [items],
  );
  // Every page any of this is drawn on — the tools' own crops and the crops of everything
  // they touch, since a ruler's box is measured on the same page as the thing it sizes.
  const pages = useMemo(() => {
    const of = new Set<number>(tools);
    for (const at of tools) {
      for (const crop of schema.byPart[at] ?? []) for (const j of crop.touches ?? []) of.add(j);
    }
    return cropPages(schema.byPart, [...of]);
  }, [schema.byPart, tools]);
  const aspects = usePageAspects(file, pages);

  // The longest side of every part's 3D model, by catalog item — the ruler this prefers to
  // anything the manual printed. A barrel nut's printed size is "M6, slotted drive", which
  // measures nothing; its mesh is 16 mm long and always was.
  const meshDims = useMeshDims(useMemo(
    () => items
      .map((p) => ({
        itemId: p.catalogId as number,
        dims: partDbSize(catalog, p.catalogId ?? null, p.size ?? null) as Dims,
      }))
      .filter((w) => w.itemId != null),
    [items, catalog],
  ));
  const meshLongest = useMemo(
    () => new Map([...meshDims].map(([id, d]) => [id, longestMesh(d)])),
    [meshDims],
  );

  const reads = useMemo(() => {
    const out: Record<number, ToolRead[]> = {};
    for (const at of tools) {
      out[at] = toolReads(at, items, catalog, schema.byPart, aspects, meshLongest);
    }
    return out;
  }, [tools, items, catalog, schema.byPart, aspects, meshLongest]);

  // The length the drawings give a tool, or null where they give none. NOT WHILE THE SEARCH
  // IS OUT: a run empties the boxes before it refills them, so mid-run a length would be read
  // off whatever fraction of the drawings has come back.
  const lengthOf = (at: number): number | null =>
    (schema.busy ? null : toolLength(reads[at] ?? []));

  // WRITTEN INTO THE PARTS, because that is the only way it reaches anything: the mesh viewer
  // reads a part's size. `lengthMm` and nothing else — a drawing gives one length and no axes,
  // and mmPerUnit anchors a mesh off exactly that (see meshAnnotations).
  //
  // Saved in two places, because they answer two different questions. The part carries it
  // for this manual, which is what the mesh viewer reads. The Parts-DB row carries it for
  // every other manual — and it is the one that survives a re-run of Phase 2, which rebuilds
  // the parts list from the page and would drop this with it. The server refuses to let a
  // derived size displace a measured one, so this cannot overwrite a real measurement.
  const save = (at: number): boolean => {
    const mm = lengthOf(at);
    if (mm == null) return false;
    const read = reads[at] ?? [];
    const note = t('notice.parts.toolSizeNote')
      .replace('{n}', String(read.length))
      .replace('{parts}', [...new Set(read.map((r) => partName(items, r.ref)))].join(', '));
    const patch = { lengthMm: Math.round(mm), source: 'derived' as const, note };
    setPartSize(docId, at, patch);
    const itemId = items[at]?.catalogId;
    if (itemId != null) {
      void fillCatalogSizes([{
        catalogId: itemId,
        size: { ...(items[at].size ?? {}), ...patch } as PartSize,
      }]);
    }
    return true;
  };

  // WHAT THE LENGTH BECOMES, in the viewer's words: the axis of the mesh's longest extent,
  // which is the one mmPerUnit will scale against. Empty where the part has no model.
  const axisWord = (i: number): string => {
    const itemId = items[i]?.catalogId;
    const mesh = itemId == null ? undefined : meshDims.get(itemId);
    if (!mesh) return '';
    return t({ x: 'notice.parts.axisWidth', y: 'notice.parts.axisDepth', z: 'notice.parts.axisHeight' }[
      longestAxis(mesh)
    ]);
  };

  // THE WORKING, for Estimate's panel: every tool asked about, each with the sum it came to —
  // or the line saying the drawings gave it none, which is when the tool is sized from its
  // standard instead.
  const panel = (of: number[]): ReactNode => (
    <div className="space-y-6">
      {of.map((i) => {
        const read = reads[i] ?? [];
        const mm = lengthOf(i);
        const name = (language === 'fr' ? items[i]?.nameFr : items[i]?.nameEn) || items[i]?.name || '';
        return (
          <section key={i} className="space-y-2">
            <h4 className="text-center text-[11px] font-bold uppercase tracking-wide text-slate-300">
              {partName(items, i)}
            </h4>
            {mm == null ? (
              <p className="text-center text-sm text-slate-400">{t('notice.parts.toolSizeNoRef')}</p>
            ) : (
              <Derivation
                read={read}
                items={items}
                mm={mm}
                t={t}
                saveLabel={t('notice.parts.toolSizeSaveOne')
                  .replace('{mm}', String(Math.round(mm)))
                  .replace('{part}', name)
                  + (axisWord(i) ? t('notice.parts.toolSizeAsAxis').replace('{axis}', axisWord(i)) : '')}
                onSave={() => { save(i); }}
              />
            )}
          </section>
        );
      })}
    </div>
  );

  return { lengthOf, save, panel };
}

// Widest over narrowest reading, or 1 where there is only one.
const spread = (read: ToolRead[]): number => {
  const got = read.map((r) => r.lengthMm).filter((v) => v > 0);
  return got.length > 1 ? Math.max(...got) / Math.min(...got) : 1;
};

const partName = (items: PartItem[], at: number): string => {
  const p = items[at];
  if (!p) return `#${at}`;
  return `${p.nameEn || p.name || `#${at}`}${p.letter ? ` (${p.letter})` : ''}`;
};

// EVERY READING, WRITTEN AS THE SUM IT IS. A number a reader cannot argue with is a number
// they have to believe, and this one is three multiplications deep: the reference's own
// millimetres, its box, the tool's box. Named in that order, per drawing, with the median
// they resolve to at the end.
function Derivation({ read, items, mm, t, saveLabel, onSave }: {
  read: ToolRead[]; items: PartItem[]; mm: number; t: (k: string) => string;
  // WHAT TO DO ABOUT IT, at the foot of the argument for it. A panel that proves a number
  // and then makes you close it and find a button in a table is a panel that has stopped
  // one step short.
  saveLabel: string;
  onSave: () => void;
}) {
  const [saved, setSaved] = useState(false);
  return (
    // CENTRED, against the modal body's own left-ranged text. What this panel holds is not
    // prose to read down the left edge: it is a paragraph of preamble, two sums and a
    // conclusion, each a line long and each about the one number at the bottom.
    <div className="space-y-3 text-center text-sm">
      <p className="text-slate-300">
        {t('notice.parts.toolSizeIntro').replace('{n}', String(read.length))}
      </p>
      <ul className="space-y-2">
        {read.map((r, at) => (
          <li key={at} className="rounded-lg bg-slate-800/60 p-2">
            <div className="mb-1 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {`${t('notice.parts.stepRow')} ${r.step} · ${t('notice.pdf.page')} ${r.page}`}
            </div>
            <div className="tabular-nums text-slate-300">
              {partName(items, r.ref)} — {Math.round(r.refMm)} mm
              {' ÷ '}{r.refSide.toFixed(3)}
              {' = '}{Math.round(r.mmPerUnit)} mm{t('notice.parts.toolSizePerUnit')}
            </div>
            <div className="tabular-nums text-slate-300">
              {t('notice.parts.toolSizeTool')} {r.toolSide.toFixed(3)}
              {' × '}{Math.round(r.mmPerUnit)}
              {' = '}<span className="font-semibold text-blue-400">
                {Math.round(r.lengthMm)} mm
              </span>
            </div>
          </li>
        ))}
      </ul>
      <p className="font-semibold text-slate-200">
        {t('notice.parts.toolSizeMedian').replace('{mm}', String(Math.round(mm)))}
      </p>
      {/* WHEN THE READINGS DISAGREE, said out loud. The whole method assumes the drawing is
          to one scale, and a manual that draws its hardware oversized breaks that quietly —
          a median of two numbers a factor apart is a confident answer to a question nobody
          can answer from these pictures. The spread is the only way to see it. */}
      {spread(read) > 1.5 && (
        <p className="text-amber-400">
          {t('notice.parts.toolSizeSpread').replace('{n}', spread(read).toFixed(1))}
        </p>
      )}
      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={() => { onSave(); setSaved(true); }}
          disabled={saved}
          className="inline-flex items-center gap-2 rounded-lg border border-blue-500/60 px-3 py-2 text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-500/10 disabled:cursor-default disabled:border-slate-600 disabled:text-slate-400"
        >
          <Ruler className="h-4 w-4" />
          {saved ? t('notice.parts.toolSizeSavedOne') : saveLabel}
        </button>
      </div>
    </div>
  );
}
